import {captureException} from '@sentry/browser';
import {Card, DataStatus, IconButton} from 'platform/components';
import {Text, HStack, Icon, VStack, Show, Link, Hide} from 'platform/foundation';
import {match} from 'ts-pattern';

import {always} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {useGetCustomerV2Query} from '@dms/api/customer';
import {EntityResourceIds} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {
  customerRoutes,
  tiresInventoryRoutes,
  vehiclesRoutes,
  warehouseRoutes,
  workshopRoutes,
} from '@dms/routes';
import {getCustomerName} from '@dms/shared';

import {composePath, RequiredTestIdProps, useNavigate} from 'shared/web';

import {TaskRelationProps} from '../../types/TaskRelationProps';
import {getIconKeyByTaskRelationType} from '../../utils/getIconKeyByTaskRelationType';
import {useTaskRelationBusinessCase} from './hooks/useTaskRelationBusinessCase';
import {useTaskRelationDirectSale} from './hooks/useTaskRelationDirectSale';
import {useTaskRelationInterest} from './hooks/useTaskRelationInterest';
import {useTaskRelationServiceCase} from './hooks/useTaskRelationServiceCase';
import {useTaskRelationTireOrder} from './hooks/useTaskRelationTireOrder';
import {useTaskRelationVehicle} from './hooks/useTaskRelationVehicle';

interface TaskRelatesCardProps extends RequiredTestIdProps, Required<TaskRelationProps> {
  onRelationRemove: () => void;
  isReadOnly?: boolean;
}

export function TaskRelatesCard(props: TaskRelatesCardProps) {
  const navigate = useNavigate();

  const {
    interest,
    customerId: interestCustomerId,
    isLoading: isInterestLoading,
    isError: hasInterestError,
    pathname: interestPathname,
  } = useTaskRelationInterest(
    props.resourceId === EntityResourceIds.interest ? props.relatedRecordId : ''
  );
  const {
    businessCase,
    customerId: bcCustomerId,
    isLoading: isBcLoading,
    isError: hasBcError,
    pathname: bcPathname,
  } = useTaskRelationBusinessCase(
    props.resourceId === EntityResourceIds.businessCase ? props.relatedRecordId : ''
  );

  const {
    vehicle,
    vehicleTitle,
    isLoading: isVehicleLoading,
    isError: hasVehicleError,
  } = useTaskRelationVehicle(
    props.resourceId === EntityResourceIds.vehicle ? props.relatedRecordId : ''
  );

  const {
    serviceCase,
    isServiceCaseDraft,
    customerId: serviceCaseCustomerId,
    isLoading: isServiceCaseLoading,
    isError: hasServiceCaseError,
  } = useTaskRelationServiceCase(
    props.resourceId === EntityResourceIds.serviceCase ? props.relatedRecordId : ''
  );

  const {
    directSale,
    isLoading: isDirectSaleLoading,
    isError: hasDirectSaleError,
  } = useTaskRelationDirectSale(
    props.resourceId === EntityResourceIds.directSale ? props.relatedRecordId : ''
  );

  const {
    tireOrder,
    customerId: tireOrderCustomerId,
    isLoading: isTireOrderLoading,
    isError: hasTireOrderError,
  } = useTaskRelationTireOrder(
    props.resourceId === EntityResourceIds.tireOrder ? props.relatedRecordId : ''
  );

  // Get Customer
  const customerId =
    interestCustomerId ?? bcCustomerId ?? serviceCaseCustomerId ?? tireOrderCustomerId;
  const {
    data: customer,
    isLoading: isCustomerLoading,
    isError: isCustomerErrored,
  } = useGetCustomerV2Query(
    {customerId: customerId ?? props.relatedRecordId},
    {
      skip: isNilOrEmpty(customerId) || props.resourceId === EntityResourceIds.customer,
    }
  );

  const title = match(props.resourceId)
    .returnType<string>()
    .with(
      EntityResourceIds.customer,
      () => getCustomerName(customer) ?? i18n.t('entity.customer.labels.customer')
    )
    .with(
      EntityResourceIds.vehicle,
      () => vehicleTitle ?? vehicle?.title ?? i18n.t('entity.vehicle.labels.vehicle')
    )
    .with(
      EntityResourceIds.businessCase,
      () => businessCase?.code ?? i18n.t('page.businessCase.labels.businessCase')
    )
    .with(
      EntityResourceIds.interest,
      () => interest?.publicId ?? i18n.t('entity.interest.labels.interest')
    )
    .with(
      EntityResourceIds.serviceCase,
      () => serviceCase?.number ?? i18n.t('page.workshop.labels.serviceCase')
    )
    .with(
      EntityResourceIds.directSale,
      () => directSale?.number ?? i18n.t('entity.warehouse.labels.directSale')
    )
    .with(
      EntityResourceIds.tireOrder,
      () => tireOrder?.number ?? i18n.t('page.tiresInventory.labels.tireOrder')
    )
    .otherwise(always(''));

  const entityPathname = match(props.resourceId)
    .with(EntityResourceIds.customer, () =>
      composePath(customerRoutes.detail, {params: {id: props.relatedRecordId}})
    )
    .with(EntityResourceIds.vehicle, () =>
      composePath(vehiclesRoutes.detail, {params: {id: props.relatedRecordId}})
    )
    .with(EntityResourceIds.businessCase, () => bcPathname)
    .with(EntityResourceIds.interest, () => interestPathname)
    .with(EntityResourceIds.serviceCase, () =>
      composePath(
        isServiceCaseDraft ? workshopRoutes.draftServiceCase : workshopRoutes.serviceCaseDetail,
        {params: {id: props.relatedRecordId}}
      )
    )
    .with(EntityResourceIds.directSale, () =>
      composePath(warehouseRoutes.directSalesDetailOverview, {params: {id: props.relatedRecordId}})
    )
    .with(EntityResourceIds.tireOrder, () =>
      composePath(tiresInventoryRoutes.tireOrderDetail, {params: {id: props.relatedRecordId}})
    )
    .otherwise(always(new Error(`Task unknown resource id - ${props.resourceId}`)));

  if (entityPathname instanceof Error) {
    captureException(entityPathname, {
      extra: {
        resourceId: props.resourceId,
        relatedRecordId: props.relatedRecordId,
      },
    });
    return null;
  }

  const isLoading =
    isCustomerLoading ||
    isInterestLoading ||
    isBcLoading ||
    isVehicleLoading ||
    isServiceCaseLoading ||
    isTireOrderLoading ||
    isDirectSaleLoading;
  const isError =
    isCustomerErrored ||
    hasInterestError ||
    hasBcError ||
    hasVehicleError ||
    hasServiceCaseError ||
    hasTireOrderError ||
    hasDirectSaleError;

  return (
    <Card variant="inlineGrey" data-testid={props['data-testid']}>
      <DataStatus isLoading={isLoading} isError={isError}>
        <HStack justify="space-between" align="center">
          <HStack spacing={4} align="center">
            <Icon value={getIconKeyByTaskRelationType(props.resourceId)} size={6} />
            <VStack>
              <Link
                onClick={() => {
                  navigate(entityPathname);
                }}
                size="small"
                title={title}
              />
              <Show when={customer && props.resourceId !== EntityResourceIds.customer}>
                <Text size="xSmall" color="secondary">
                  {`${i18n.t('entity.customer.labels.customer')}: ${getCustomerName(customer)}`}
                </Text>
              </Show>
              <Show when={directSale && props.resourceId === EntityResourceIds.directSale}>
                <Text size="xSmall" color="secondary">
                  {directSale?.variantName}
                </Text>
              </Show>
            </VStack>
          </HStack>
          <Hide when={props.isReadOnly}>
            <IconButton icon="navigation/close" onClick={props.onRelationRemove} />
          </Hide>
        </HStack>
      </DataStatus>
    </Card>
  );
}
